import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { createPredefinedComments } from "../../api/recognitionApi";
import { useLocalization } from "../../context/LocalizationContext";
import { useToast } from "../../context/ToastContext";
import { useLocalText } from "../common/useLocalText";
import { usePermissions } from "../../hooks/usePermissions";
import LocalText from "../common/LocalText";
import CommentCard from "./CommentCard";
import CommentModal from "./CommentModal";
import StatusTabs from "../approval/StatusTabs";
import {
  buildVersionedList,
  filterVersionedList,
  countVersionedTabs,
} from "../../utils/versionedContent";

const CommentList = ({ comments, onSuccess }) => {
  const { availableLocales } = useLocalization();
  const { toast } = useToast();
  const { canFeature } = usePermissions();
  const canEdit = canFeature('/recognition', 'recognition-edit');
  const searchPlaceholder = useLocalText("Search comments...");

  const [search, setSearch] = useState("");
  const [tabValue, setTabValue] = useState("live");
  const [highlight, setHighlight] = useState({ tab: null, token: 0 });
  const flashTab = useCallback((tab) => setHighlight({ tab, token: Date.now() }), []);
  const [createOpen, setCreateOpen] = useState(false);

  const versioned = useMemo(() => buildVersionedList(comments), [comments]);
  const counts    = useMemo(() => countVersionedTabs(comments), [comments]);

  const filtered = useMemo(() => {
    const tabItems = filterVersionedList(versioned, tabValue);
    const q = search.trim().toLowerCase();
    if (!q) return tabItems;
    return tabItems.filter((c) =>
      c.localizedPairs?.values?.some((v) =>
        v.value?.toLowerCase().includes(q)
      )
    );
  }, [versioned, search, tabValue]);

  const handleCreate = async (localizedPairs) => {
    try {
      await createPredefinedComments({
        isActive: true,
        localizations: localizedPairs.values,
      });
      await onSuccess();
      toast.success("Comment added");
      flashTab('draft');
    } catch {
      // error toast from api interceptor
    }
  };

  return (
    <>
      {/* Status tabs — Live / Pending / Draft / Rejected */}
      <StatusTabs
        counts={counts}
        value={tabValue}
        onChange={setTabValue}
        highlightTab={highlight.tab}
        highlightToken={highlight.token}
      />

      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          gap: 2,
          mb: 4,
        }}
      >
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: "#85a5c6" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flexGrow: 1,
            maxWidth: 380,
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              bgcolor: "white",
              "& fieldset": { borderColor: "#dde6f0" },
              "&:hover fieldset": { borderColor: "#5383b3" },
              "&.Mui-focused fieldset": { borderColor: "#0a326d" },
            },
          }}
        />

        <Box display="flex" alignItems="center" gap={3}>
          {/* Active entries count — hidden for now
          <Typography
            variant="caption"
            sx={{
              color: "#85a5c6",
              fontWeight: 600,
              display: { xs: "none", sm: "block" },
            }}
          >
            {comments.filter((c) => c.isActive).length}{" "}
            <LocalText>active entries</LocalText>
          </Typography>
          */}

          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={(e) => {
                e.currentTarget.blur();
                setCreateOpen(true);
              }}
              sx={{
                bgcolor: "#0a326d",
                color: "white",
                borderRadius: "12px",
                px: 2.5,
                py: 1,
                fontWeight: 600,
                fontSize: 13,
                textTransform: "none",
                whiteSpace: "nowrap",
                boxShadow: "0 4px 12px rgba(10,50,109,0.25)",
                "&:hover": {
                  bgcolor: "#0d3d85",
                  boxShadow: "0 6px 16px rgba(10,50,109,0.35)",
                },
              }}
            >
              <LocalText>Create Recognition</LocalText>
            </Button>
          )}
        </Box>
      </Box>

      {/* Empty state */}
      {filtered.length === 0 && (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <ChatBubbleOutlineIcon
            sx={{ fontSize: 52, color: "#c8d8ea", mb: 1.5 }}
          />
          <Typography variant="body1" sx={{ color: "#85a5c6", fontWeight: 500 }}>
            {search ? (
              <LocalText>No comments match your search</LocalText>
            ) : (
              <LocalText>No comments yet. Create one!</LocalText>
            )}
          </Typography>
        </Box>
      )}

      {/* Cards grid */}
      <Grid container spacing={3}>
        {filtered.map((comment) => (
          <Grid
            size={{ xs: 12, sm: 6, md: 4 }}
            key={
              comment._viewType === "snapshot"
                ? `snap-${comment._liveId ?? comment.id}`
                : comment.id
            }
          >
            <CommentCard comment={comment} onSuccess={(targetTab) => { onSuccess(); if (targetTab) flashTab(targetTab); }} />
          </Grid>
        ))}
      </Grid>

      {/* Create modal */}
      <CommentModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSave={handleCreate}
        locales={availableLocales}
      />
    </>
  );
};

export default CommentList;
